import React from 'react';
import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://gorito.com';
const DEFAULT_IMAGE = 'https://gorito.com/og-image.jpg';
const SITE_NAME = 'Gorito Fine Arts';

/**
 * SEO Component for managing meta tags dynamically
 * 
 * @param {Object} props
 * @param {string} props.title - Page title
 * @param {string} props.description - Meta description
 * @param {string} props.image - OG image URL
 * @param {string} props.url - Canonical URL path (e.g., "/collections/paintings")
 * @param {string} props.type - OG type (website, product, article)
 * @param {Object} props.product - Product data for structured data
 * @param {Object} props.article - Article data for structured data
 */
const SEO = ({ 
  title, 
  description, 
  image, 
  url = '', 
  type = 'website',
  product = null,
  article = null,
  noindex = false
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Original Paintings and Fine Art Prints`;
  const fullUrl = `${BASE_URL}${url}`;
  const imageUrl = image || DEFAULT_IMAGE;
  const metaDescription = description || 'Discover original paintings and fine art prints by Swedish artist Gorito. Shop unique artwork including portraits, kaleidoscopes, and nature-inspired pieces. Worldwide shipping.';

  // Generate Product structured data (JSON-LD)
  const generateProductSchema = () => {
    if (!product) return null;
    
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": product.title,
      "description": product.description,
      "image": product.image,
      "brand": {
        "@type": "Brand",
        "name": "Gorito Fine Arts"
      },
      "offers": {
        "@type": "Offer",
        "url": fullUrl,
        "priceCurrency": product.currency || "SEK",
        "price": product.price,
        "availability": product.sold 
          ? "https://schema.org/SoldOut" 
          : product.inStock 
            ? "https://schema.org/InStock" 
            : "https://schema.org/OutOfStock",
        "seller": {
          "@type": "Organization",
          "name": "Gorito Fine Arts"
        }
      },
      "category": product.artworkType === 'painting' ? "Original Painting" : "Fine Art Print"
    };
  };

  // Generate Article structured data (JSON-LD)
  const generateArticleSchema = () => {
    if (!article) return null;
    
    return {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": article.excerpt,
      "image": article.image,
      "datePublished": article.date,
      "dateModified": article.updatedAt || article.date,
      "author": {
        "@type": "Person",
        "name": article.author || "Gorito"
      },
      "publisher": {
        "@type": "Organization",
        "name": "Gorito Fine Arts",
        "logo": {
          "@type": "ImageObject",
          "url": `${BASE_URL}/logo.png`
        }
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": fullUrl
      }
    };
  };

  // Organization structured data (on homepage)
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Gorito Fine Arts",
    "url": BASE_URL,
    "logo": `${BASE_URL}/logo.png`,
    "description": "Original paintings and fine art prints by Swedish artist Gorito",
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "SE"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": "info@gorito.com"
    },
    "sameAs": []
  };

  // Breadcrumb structured data
  const generateBreadcrumbSchema = () => {
    const pathParts = url.split('/').filter(Boolean);
    if (pathParts.length === 0) return null;

    const items = [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": BASE_URL
      }
    ];

    let currentPath = '';
    pathParts.forEach((part, index) => {
      currentPath += `/${part}`;
      items.push({
        "@type": "ListItem",
        "position": index + 2,
        "name": part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, ' '),
        "item": `${BASE_URL}${currentPath}`
      });
    });

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": items
    };
  };

  const productSchema = generateProductSchema();
  const articleSchema = generateArticleSchema();
  const breadcrumbSchema = generateBreadcrumbSchema();

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={fullUrl} />
      
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Structured Data - Organization (only on homepage) */}
      {url === '' && (
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
      )}

      {/* Structured Data - Product */}
      {productSchema && (
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      )}

      {/* Structured Data - Article */}
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}

      {/* Structured Data - Breadcrumbs */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
